import type { ZodType } from "zod";
import { getBackendOrigin } from "../../../server/brand-core.mjs";

const DEFAULT_TIMEOUT_MS = 15_000;
const MAX_RATE_LIMIT_RETRIES = 2;
const MAX_RETRY_DELAY_MS = 5_000;
export const AUTH_INVALID_EVENT = "monvravex:auth-invalid";

type RequestOptions = Omit<RequestInit, "credentials"> & {
  timeoutMs?: number;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, details?: unknown) {
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function getApiBaseUrl(hostname = typeof window === "undefined" ? "" : window.location.hostname) {
  if (import.meta.env.DEV && ["localhost", "127.0.0.1", "[::1]"].includes(hostname)) {
    const origin: unknown = import.meta.env.VITE_DEV_API_ORIGIN;
    if (typeof origin === "string" && origin) return new URL(origin).origin;
  }
  return getBackendOrigin(hostname);
}

export function resolveApiMediaUrl(value: string) {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${getApiBaseUrl()}${value.startsWith("/") ? value : `/${value}`}`;
}

/**
 * The avatar image lives behind the cookie-authenticated `GET /api/user/{id}.pic` resource, not at the raw
 * `avatar` field; `version` (pass the raw `avatar` field) only busts the browser cache after a re-upload.
 */
export function resolveAvatarUrl(userId: number | string, version?: string) {
  const base = `${getApiBaseUrl()}/api/user/${userId}.pic`;
  return version ? `${base}?v=${encodeURIComponent(version)}` : base;
}

async function parseResponseBody(response: Response) {
  const responseText = await response.text();

  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText) as unknown;
  } catch {
    return responseText;
  }
}

function getErrorCode(payload: unknown, status: number) {
  if (typeof payload === "string" && payload.trim()) {
    return payload.trim().replace(/^"|"$/g, "");
  }

  if (payload && typeof payload === "object") {
    const errorPayload = payload as Record<string, unknown>;
    const candidate = errorPayload.code ?? errorPayload.error ?? errorPayload.detail ?? errorPayload.message;

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return `http_${status}`;
}

function getRetryDelayMs(response: Response, attempt: number) {
  const header = response.headers.get("retry-after");
  const seconds = header === null ? NaN : Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const timestamp = header === null ? NaN : Date.parse(header);
  if (Number.isFinite(timestamp)) return Math.max(0, timestamp - Date.now());
  return 800 * 2 ** attempt;
}

function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const abort = () => {
      globalThis.clearTimeout(timeoutId);
      signal.removeEventListener("abort", abort);
      reject(signal.reason instanceof Error ? signal.reason : new DOMException("Aborted", "AbortError"));
    };
    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
  });
}

export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {}
): Promise<T> {
  if (options.signal?.aborted) throw options.signal.reason ?? new DOMException("Aborted", "AbortError");
  const requestController = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => requestController.abort(new DOMException("Request timed out", "TimeoutError")),
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  );
  const abortFromCaller = () => requestController.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", abortFromCaller, { once: true });

  try {
    // Only idempotent reads are retried on 429; mutations (payments, withdrawals) are never repeated automatically.
    const requestPath = new URL(path, "https://api.invalid").pathname;
    const isRead = (options.method ?? "GET").toUpperCase() === "GET" &&
      !["/api/resend_email", "/api/check_key", "/api/user/popup_notifications/fetch"].includes(requestPath) &&
      !(requestPath === "/api/user/notifications/fetch" && new URL(path, "https://api.invalid").searchParams.get("mark_read") === "true");
    let response: Response;
    for (let attempt = 0; ; attempt += 1) {
      const demoResponse = import.meta.env.DEV && import.meta.env.VITE_DEV_AUCTION === "1"
        ? (await import("./dev-auction")).handleDevAuction(path, options)
        : null;
      const base = import.meta.env.DEV && import.meta.env.VITE_DEV_API_ORIGIN &&
        ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)
        ? "/__dev/backend" : getApiBaseUrl();
      response = demoResponse ?? await fetch(`${base}${path}`, {
        ...options,
        signal: requestController.signal,
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...options.headers
        }
      });
      if (!isRead || response.status !== 429 || attempt >= MAX_RATE_LIMIT_RETRIES) break;
      const delay = getRetryDelayMs(response, attempt);
      if (delay > MAX_RETRY_DELAY_MS) break;
      await sleep(delay, requestController.signal);
    }
    const payload = await parseResponseBody(response);
    if (requestController.signal.aborted) throw requestController.signal.reason ?? new DOMException("Aborted", "AbortError");

    if (!response.ok) {
      const code = getErrorCode(payload, response.status);
      if (response.status === 401 || (response.status === 403 && code.toLowerCase().includes("not authenticated"))) {
        window.dispatchEvent(new Event(AUTH_INVALID_EVENT));
      }
      throw new ApiError(response.status, code, payload);
    }

    const parsed = schema.safeParse(payload);

    if (!parsed.success) {
      throw new ApiError(502, "invalid_api_response", parsed.error.flatten());
    }

    return parsed.data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (requestController.signal.reason instanceof DOMException && requestController.signal.reason.name === "TimeoutError") {
      throw new ApiError(0, "request_timeout");
    }
    if (options.signal?.aborted) throw error;
    throw new ApiError(0, "network_error");
  } finally {
    globalThis.clearTimeout(timeoutId);
    options.signal?.removeEventListener("abort", abortFromCaller);
  }
}

export function createFormBody(values: Record<string, string | number | boolean | null | undefined>) {
  const body = new URLSearchParams();

  for (const [key, value] of Object.entries(values)) {
    if (value !== null && value !== undefined) {
      body.set(key, String(value));
    }
  }

  return body;
}

/** True when the backend does not implement an endpoint yet (missing route / method / not implemented). */
export function isEndpointUnavailable(error: unknown) {
  return error instanceof ApiError && (error.status === 404 || error.status === 405 || error.status === 501);
}
